import { createFileRoute, isRedirect, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { Card, CardContent, CardHeader, CardTitle } from "@beweegbaas/ui";
import { m } from "#/paraglide/messages";
import { captureUnexpectedError } from "#/modules/util.errors/capture";
import { PasskeyLogin } from "#/modules/generic.login/components/button.passkey-login";
import { LayoutAuth } from "#/layouts/layout.auth";
import { loginFn, prepareLogin, redirectIfAuthenticated } from "#/modules/util.auth/auth.functions";
import { loginSchema } from "#/modules/generic.login/schema.login";
import { useAppForm } from "#/modules/util.forms/form";
import { z } from "zod/v4";

export const Route = createFileRoute("/$locale/{-$schoolyear}/login")({
  // Optional keys, not `string | undefined` ones: the latter are *required* to TS, forcing every
  // plain link to /login to spell out `search={{ ... }}`.
  validateSearch: z.object({
    referer: z.string().optional().catch(undefined),
    email: z.string().optional().catch(undefined),
    schoolName: z.string().optional().catch(undefined),
    type: z.string().optional().catch(undefined),
  }),
  // Already logged in? Send the user to their post-login destination (portal parity).
  // Never during a preload: preloadRoute() follows the thrown redirect and runs school-route
  // loaders while the browser is still on a tenant-less page (e.g. /confirm-email), so their
  // API calls go out without tenant headers (MissingTenantHeader). Real navigation redirects
  // as before.
  beforeLoad: async ({ context, preload }) => {
    if (context.user && !preload) {
      await redirectIfAuthenticated();
    }
  },
  loaderDeps: ({ search }) => ({ email: search.email }),
  loader: ({ deps }) => prepareLogin({ data: { email: deps.email } }),
  component: LoginPage,
});

function LoginPage() {
  const defaults = Route.useLoaderData();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const login = useServerFn(loginFn);

  const form = useAppForm({
    defaultValues: { email: defaults.email, password: defaults.password },
    validators: { onSubmit: loginSchema },
    onSubmit: async ({ value }) => {
      try {
        // Pass the attempted-URL (referer) so loginCallback returns the user there.
        const res = await login({ data: { ...value, referer: search.referer } });
        if (res && "formError" in res) {
          form.setErrorMap({ onSubmit: { form: res.formError, fields: {} } });
          return;
        }
        if (res?.requiresTwoFactor) {
          void navigate({
            from: Route.fullPath,
            to: "/$locale/{-$schoolyear}/login-2fa",
            params: (prev) => prev,
            search: { email: value.email },
          });
        }
      } catch (error) {
        if (isRedirect(error)) throw error;
        captureUnexpectedError(error, "login");
        form.setErrorMap({ onSubmit: { form: m["common.something_went_wrong"](), fields: {} } });
      }
    },
  });

  return (
    <LayoutAuth>
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Inloggen</CardTitle>
        </CardHeader>
        <CardContent>
          <form.AppForm>
            <form.FormRoot className="flex flex-col gap-4">
              <form.AppField
                name="email"
                children={(field) => (
                  <field.TextField label="E-mailadres" autoComplete="username webauthn" />
                )}
              />
              <form.AppField
                name="password"
                children={(field) => <field.PasswordTextField label="Wachtwoord" />}
              />

              <div className="flex justify-end text-sm">
                <Link
                  from={Route.fullPath}
                  className="text-blue-600 hover:underline dark:text-blue-400"
                  to="/$locale/{-$schoolyear}/forgot-password"
                  params={(prev: Record<string, string>) => prev}
                >
                  Wachtwoord vergeten?
                </Link>
              </div>

              <form.SubmitButton className="w-full">Inloggen</form.SubmitButton>
            </form.FormRoot>
          </form.AppForm>

          <PasskeyLogin referer={search.referer} />
        </CardContent>
      </Card>
    </LayoutAuth>
  );
}
